"use client";

import Image from "next/image";
import { m } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { useContentView } from "@/hooks/useContentView";
import { useIsDark } from "@/context/ThemeContext";
import Button from "@/components/common/Button";

import { aiNexusFeatures, aiNexusMonitor } from "@/constants/Data";

const AI_NEXUS_URL = "https://ai-nexus.icieos.com/";

const CARD_GLOW =
  "radial-gradient(67% 100% at 50% 0%, rgba(255,28,247,0.08) 0%, rgba(255,28,247,0) 72.72%, rgba(16,36,219,0) 100%)";

const gradientBorder = (surface, accent, stop) =>
  `linear-gradient(${surface}, ${surface}) padding-box, ` +
  `linear-gradient(0deg, ${accent} ${stop}, rgba(255,255,255,0.15) 100%) border-box`;

export default function AiNexus() {
  const isDark = useIsDark();
  const contentViewRef = useContentView("AI Nexus");

  const CARD_BORDER = isDark
    ? gradientBorder("#000000", "#161616", "11.81%")
    : gradientBorder("#F9FBFD", "#FF1CF7", "17.86%");

  const fadeInUpHeaders = {
    initial: {
      filter: "saturate(0) contrast(4) brightness(0.1) blur(5px)",
      opacity: 0,
      scale: 0.95,
    },
    whileInView: {
      filter: "none",
      opacity: 1,
      scale: 1,
    },
    viewport: { once: true, margin: "-5% 0px -5% 0px", amount: 0.8 },
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  };

  const fadeInUpCards = {
    initial: {
      filter: "saturate(0) contrast(4) brightness(0.1) blur(5px)",
      opacity: 0,
      scale: 0.95,
    },
    whileInView: {
      filter: "none",
      opacity: 1,
      scale: 1,
    },
    viewport: { once: true, amount: 0.4 },
    transition: {
      duration: 0.9,
      ease: [0.25, 0.1, 0.25, 1],
    },
  };

  return (
    <section
      ref={contentViewRef}
      className="mx-5 md:mx-10 xl:mx-[100px] 3xl:mx-[150px]"
    >
      <div
        className={twMerge(
          "flex flex-col gap-6 xl:gap-[34px] rounded-[20px] md:rounded-[28px] xl:rounded-[40px] p-6 md:p-10 xl:p-[48px] 3xl:p-[64px]",
          isDark ? "bg-white/8" : "bg-white"
        )}
      >
        {/* the heading spans the full panel so it stays on a single line */}
        <m.h2
          {...fadeInUpHeaders}
          style={{ willChange: "opacity, transform" }}
          className="font-alexandria font-semibold uppercase text-[22px] sm:text-[24px] md:text-[28px] lg:text-[32px] xl:text-[35px] 3xl:text-[40px] leading-normal lg:whitespace-nowrap"
        >
          OUR FLAGSHIP PLATFORM - AI NEXUS 
        </m.h2>

        <div className="flex flex-col-reverse lg:flex-row items-center gap-8 lg:gap-10 lg:gap-6 xl:gap-10">
          {/* copy, feature cards and CTA */}
          <div className="flex flex-col gap-6 xl:gap-[34px] w-full lg:w-[50%]">
            <m.p
              {...fadeInUpHeaders}
              style={{ willChange: "opacity, transform" }}
              className={twMerge(
                "font-manrope font-medium text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px] 3xl:text-[22px] leading-normal xl:max-w-[554px]",
                isDark ? "text-[#EEEEFF]" : "text-[#757676]"
              )}
            >
              AI Nexus connects with your existing business systems and data to give
              leaders a unified intelligence layer for analysis, decision-making and action.
              Ask questions, investigate performance, identify risks and uncover
              opportunities through dashboards, analytics and conversational AI.
            </m.p>

            <div className="grid grid-cols-1 mobile2:grid-cols-2 gap-x-[22px] gap-y-[19px] xl:max-w-[555px]">
              {aiNexusFeatures.map((feature) => (
                <m.div
                  key={feature.title}
                  {...fadeInUpCards}
                  style={{
                    willChange: "opacity, transform",
                    background: CARD_BORDER,
                  }}
                  className="relative overflow-hidden rounded-[16px] border-2 border-transparent p-[15px] pt-[18px] transition-all duration-100 ease-in hover:shadow-[0_4px_4px_#FF1CF733]"
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 h-[137px]"
                    style={{ backgroundImage: CARD_GLOW }}
                  />

                  <div className="relative flex items-start gap-[10px]">
                    <div className="flex flex-col gap-[5px] grow">
                      <p
                        className={twMerge(
                          "font-inter font-medium text-[15px] xl:text-[16px] leading-[28.8px] opacity-75",
                          isDark ? "text-[#FFFFFF]" : "text-[#1A1A1A]"
                        )}
                      >
                        {feature.title}
                      </p>
                      <p
                        className={twMerge(
                          "font-inter font-normal text-[14px] xl:text-[16px] leading-[22px] opacity-75",
                          isDark ? "text-[#F1F7FEB5]" : "text-[#4A4A4A]"
                        )}
                      >
                        {feature.description}
                      </p>
                    </div>

                    <div className="relative shrink-0 size-[29px] overflow-hidden">
                      <Image
                        src={feature.icon}
                        alt=""
                        aria-hidden
                        width={29}
                        height={29}
                        className="size-full"
                      />
                    </div>
                  </div>
                </m.div>
              ))}
            </div>
          </div>

          {/* product preview */}
          <m.div
            {...fadeInUpCards}
            style={{ willChange: "opacity, transform" }}
            className="w-full lg:w-[45%] flex justify-center"
          >
            <Image
              src={aiNexusMonitor}
              alt="AI Nexus Sales Intelligence dashboard shown on a desktop monitor"
              width={727}
              height={893}
              sizes="(max-width: 1400px) 90vw, 45vw"
              placeholder="blur"
              className="w-full max-w-[820px] h-auto object-contain"
            />
          </m.div>
        </div>
        <m.div
          {...fadeInUpHeaders}
          style={{ willChange: "opacity, transform" }}
        >
          <Button
            label="View More"
            href={AI_NEXUS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shadow-[1px_1px_5px_0px_#FF1CF7]"
          />
        </m.div>
      </div>
    </section>
  );
}
